import React, { useState, useEffect } from 'react'
import { LocationsContext } from './locationsContext'
import * as mwLocationService from '../minWage/components/service/getLocations'
import { useAuth } from '../auth/useAuth'
import locationService from '../services/locationService'
import wageService from '../services/wageService'
import jurisdictionService from '../services/jurisdictionService'

export const LocationsProvider = ({ children }) => {
  const auth = useAuth()
  const companyId = auth.company ? auth.company._id : null
  let allLocations = {}
  const [locationsData, setLocationsData] = useState(null)
  const [legacyMWNotificationsArray, setLegacyMWNotificationsArray] = useState([])
  const [sessionCompany, setSessionCompany] = useState(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const loadAllGeoLocations = async ( { subscribed }) => {
    const allGeoLocations = await locationService.getAllGeoLocations({
      companyId: sessionCompany._id,
      products: ['minimumWage'],
      includeProperties: ['wageSchedules']
    })

    if (subscribed) {
      const notificationsArray = buildLegacyMWNotificationsArray(allGeoLocations, sessionCompany)
      const jdUniqueNames = new Set(notificationsArray.map((notificationDto) => {
        const result = []
        if (notificationDto.wages['wage rate'] && notificationDto.wages['wage rate'].new) result.push(notificationDto.wages['wage rate'].new.jurisdictionUniqueName)
        if (notificationDto.wages.gratuities && notificationDto.wages.gratuities.new) result.push(notificationDto.wages.gratuities.new.jurisdictionUniqueName)

        return result
      })
        .flat())

      const jdUniqueNameToDocDict = await jurisdictionService.getJdUniqueNameToDocDict(Array.from(jdUniqueNames))

      if (subscribed) {
        setLegacyMWNotificationsArray(notificationsArray.map((notification) => {
          if (notification.wages['wage rate'] && notification.wages['wage rate'].new) {
            notification.wages['wage rate'].new.jurisdiction = jdUniqueNameToDocDict[notification.wages['wage rate'].new.jurisdictionUniqueName].type
            notification.wages['wage rate'].new.jurisdictionName = jdUniqueNameToDocDict[notification.wages['wage rate'].new.jurisdictionUniqueName].name
          }

          if (notification.wages.gratuities && notification.wages.gratuities.new) {
            notification.wages.gratuities.new.jurisdiction = jdUniqueNameToDocDict[notification.wages.gratuities.new.jurisdictionUniqueName].type
            notification.wages.gratuities.new.jurisdictionName = jdUniqueNameToDocDict[notification.wages.gratuities.new.jurisdictionUniqueName].name
          }

          return notification
        }))
        allLocations = getLocationsDictionary(allGeoLocations)
      }
    }
  }

  useEffect(() => {
    let subscribed = true
    if (subscribed) setSessionCompany(JSON.parse(window.sessionStorage.company || '{}'))
    return () => (subscribed = false)
  }, [window.sessionStorage.company])

  useEffect(() => {
    let subscribed = true
    setLocationsData([])
    setLegacyMWNotificationsArray([])
    setIsLoading(true)

    if (companyId && sessionCompany) {
      (loadAllGeoLocations({ subscribed }))
        .then(() => {
          if (subscribed) setLocationsData(allLocations)
          setIsLoading(false)
        })
        .catch((error) => {
          setIsLoading(false)
          console.error('Error while trying to retrieve locations', error)
        })
    }

    return () => (subscribed = false)
  }, [sessionCompany])

  return (
    <LocationsContext.Provider
      value={{ locationsData, legacyMWNotificationsArray, isLoading }}
    >
      {children}
    </LocationsContext.Provider>
  )
}

const buildLegacyMWNotificationsArray = (allLocations, sessionCompany) => {
  const reduceResult = allLocations.reduce((_dateToNotificationsDict, locationDoc) => {
    if (!locationDoc.wageSchedules) {
      console.error(`wage schedules not found for location ${locationDoc.displayName}!`)
      return _dateToNotificationsDict
    }

    // when we go to industries per location, this is going to be the thing to change if we haven't burned this page down yet
    const relevantWageSchedule = wageService.getIndustryApplicableWageSchedule(locationDoc.wageSchedules, sessionCompany)

    if (!relevantWageSchedule) {
      console.error(`relevant wage schedule not found for location ${locationDoc.displayName}!`)
      return _dateToNotificationsDict
    }

    const toLegacyNotification = (wageDoc) => ({
      amount: wageDoc.rate || null,
      jurisdictionUniqueName: wageDoc.jurisdictionUniqueName,
      state: locationDoc.addresses[0].state,
      indexed: wageDoc.isIndexed,
      law: wageDoc.law
    })

    const standardWages = relevantWageSchedule.standardSchedule
    const tippedWages = relevantWageSchedule.tippedSchedule

    const newStandardWages = standardWages.filter((wage) => (new Date(wage.effectiveDate) > new Date()))
    const newTippedWages = tippedWages.filter((wage) => (new Date(wage.effectiveDate) > new Date()))

    newStandardWages.forEach((newStandardWage) => {
      const standardWageDictKey = toLegacyDateString(newStandardWage.effectiveDate)
      if (!_dateToNotificationsDict[standardWageDictKey]) _dateToNotificationsDict[standardWageDictKey] = []
      const existingNotification = _dateToNotificationsDict[standardWageDictKey].find((notification) => (notification.wages['wage rate'] && notification.wages['wage rate'].new && notification.wages['wage rate'].new.jurisdictionUniqueName === newStandardWage.jurisdictionUniqueName)
        || (notification.wages.gratuities && notification.wages.gratuities.new && notification.wages.gratuities.new.jurisdictionUniqueName === newStandardWage.jurisdictionUniqueName))
      if (existingNotification) {
        if (!existingNotification.locations.includes(locationDoc._id)) existingNotification.locations.push(locationDoc._id)
        if (!existingNotification.wages['wage rate']) {
          existingNotification.wages['wage rate'] = { new: toLegacyNotification(newStandardWage) }
        }
      } else {
        _dateToNotificationsDict[standardWageDictKey].push({
          date: standardWageDictKey,
          locations: [locationDoc._id],
          wages: {
            'wage rate':
              { new: toLegacyNotification(newStandardWage) }
          }
        })
      }
    })

    newTippedWages.forEach((newTippedWage) => {
      const tippedWageDictKey = toLegacyDateString(newTippedWage.effectiveDate)
      if (!_dateToNotificationsDict[tippedWageDictKey]) _dateToNotificationsDict[tippedWageDictKey] = []

      const existingNotification = _dateToNotificationsDict[tippedWageDictKey].find((notification) => (notification.wages.gratuities && notification.wages.gratuities.new && notification.wages.gratuities.new.jurisdictionUniqueName === newTippedWage.jurisdictionUniqueName)
        || (notification.wages['wage rate'] && notification.wages['wage rate'].new && notification.wages['wage rate'].new.jurisdictionUniqueName === newTippedWage.jurisdictionUniqueName))
      if (existingNotification) {
        if (!existingNotification.locations.includes(locationDoc._id)) existingNotification.locations.push(locationDoc._id)
        if (!existingNotification.wages.gratuities) {
          existingNotification.wages.gratuities = { new: toLegacyNotification(newTippedWage) }
        }
      } else {
        _dateToNotificationsDict[tippedWageDictKey].push({
          date: tippedWageDictKey,
          locations: [locationDoc._id],
          wages: { gratuities: { new: toLegacyNotification(newTippedWage) } }
        })
      }
    })

    return _dateToNotificationsDict
  }, {})

  return Object.values(reduceResult)
    .flat()
}

/**
 * For translating the string that comes out of the API (e.g. "2022-01-01")
 *
 * @param dateString
 */
const toLegacyDateString = (dateString) => dateString.split('T')[0]

// This builds a list of location names, addresses and their _id.  It is used to keep the 'filter by locale' relevant.
// It used to key off of the netsuite location id (until June 13) but now uses the Mongo location id
const getLocationsDictionary = (locations) => locations && locations.reduce((_idToLocationDict, location) => {
  const address = location.addresses[0]
  const id = location._id
  _idToLocationDict[id] = {
    id,
    name: location.displayName,
    ...address
  }

  return _idToLocationDict
}, {})
